import { LoadingSpinner } from "@/components/UIComponents";
import { HubResponse } from "@/services/agenda/types";
import { refStoresService } from "@/services/refStores";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { StoreContextType } from "./types";

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export const useStore = (): StoreContextType => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return context;
};

interface StoreProviderProps {
  children: ReactNode;
}

export const StoreProvider: FC<StoreProviderProps> = ({ children }) => {
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const hubCodeFromUrl = urlSearchParams.get("hub_code");

  const [hubCode, setHubCode] = useState<string | null>("");
  const [store, setStore] = useState<HubResponse>({
    id: 0,
    name: "",
    externalCode: "",
    storeCode: "",
    phone: "",
    latitude: "",
    longitude: "",
    address: "",
    district: "",
    county: "",
    zipCode: "",
    isActive: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allStores, setAllStores] = useState<HubResponse[]>([]);
  const [isLoadingAllStores, setIsLoadingAllStores] = useState<boolean>(false);
  const [selectedStoreInScheduler, setSelectedStoreInScheduler] = useState<
    number | null
  >(null);
  const [selectedSchedulerStore, setSelectedSchedulerStore] =
    useState<HubResponse | null>(null);
  const [isLoadingSchedulerStore, setIsLoadingSchedulerStore] =
    useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);
    const extractStoreId = async () => {
      try {
        const hubCodeFromLocalStorage = localStorage.getItem("hubCode");

        // Prefer hub_code from URL whenever it's present (e.g. iframe or deep link)
        if (hubCodeFromUrl) {
          localStorage.setItem("hubCode", hubCodeFromUrl);
          const store = await refStoresService.getStoreByHubCode(
            hubCodeFromUrl,
          );
          if (store) {
            localStorage.setItem("storeExternalCode", store.externalCode);
            localStorage.setItem("storeId", store.id.toString());
            setStore(store);
            setHubCode(hubCodeFromUrl);
            setSelectedStoreInScheduler(store.id);
            return;
          }
        }

        if (hubCodeFromLocalStorage) {
          const store = await refStoresService.getStoreByHubCode(
            hubCodeFromLocalStorage,
          );
          if (store) {
            setStore(store);
            setHubCode(hubCodeFromLocalStorage);
            setSelectedStoreInScheduler(store.id);
            return;
          }
        }

        const envStoreId = Number(import.meta.env?.VITE_APPOINTMENT_STORE_ID);
        if (!isNaN(envStoreId) && envStoreId > 0) {
          const store = await refStoresService.getStoreById(envStoreId);
          if (store) {
            setStore(store);
            setHubCode(store.storeCode);
            setSelectedStoreInScheduler(store.id);
            return;
          }
        }

        const defaultStore = await refStoresService.getStoreById(5);
        if (defaultStore) {
          setStore(defaultStore);
          setHubCode(defaultStore.storeCode);
          setSelectedStoreInScheduler(defaultStore.id);
        }
      } catch (error) {
        console.error("Error extracting storeId:", error);
        setHubCode(null);
      } finally {
        setIsLoading(false);
      }
    };

    extractStoreId();
  }, [hubCodeFromUrl]);

  useEffect(() => {
    const fetchAllStores = async () => {
      setIsLoadingAllStores(true);
      try {
        const allStoresData: HubResponse[] = [];
        let currentPage = 0;
        const pageSize = 100;
        let hasMorePages = true;

        while (hasMorePages) {
          const response = await refStoresService.getAll(currentPage, pageSize);
          allStoresData.push(...response.content);

          hasMorePages = currentPage + 1 < response.page.totalPages;
          currentPage++;
        }

        setAllStores(allStoresData);
      } catch (error) {
        console.error("Error fetching all stores:", error);
      } finally {
        setIsLoadingAllStores(false);
      }
    };

    fetchAllStores();
  }, []);

  // Fetch full store data when selectedStoreInScheduler changes
  useEffect(() => {
    const fetchStoreData = async () => {
      if (!selectedStoreInScheduler) {
        setSelectedSchedulerStore(null);
        return;
      }

      setIsLoadingSchedulerStore(true);
      try {
        const storeData = await refStoresService.getStoreById(
          selectedStoreInScheduler,
        );
        setSelectedSchedulerStore(storeData);
      } catch (error) {
        console.error("Error fetching store data:", error);
        // Fallback to store from allStores if fetch fails
        const fallbackStore = allStores.find(
          s => s.id === selectedStoreInScheduler,
        );
        if (fallbackStore) {
          setSelectedSchedulerStore(fallbackStore);
        } else {
          setSelectedSchedulerStore(null);
        }
      } finally {
        setIsLoadingSchedulerStore(false);
      }
    };

    fetchStoreData();
  }, [selectedStoreInScheduler, allStores]);

  const contextValue: StoreContextType = {
    store,
    hubCode,
    isLoading,
    allStores,
    isLoadingAllStores,
    selectedStoreInScheduler,
    setSelectedStoreInScheduler,
    selectedSchedulerStore,
    isLoadingSchedulerStore,
  };

  return (
    <StoreContext.Provider value={contextValue}>
      {isLoading ? <LoadingSpinner /> : children}
    </StoreContext.Provider>
  );
};
